import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

interface PriorityData {
  critical_1: number;
  high_2: number;
  moderate_3: number;
  low_4: number;
  planning_5: number;
}

interface PriorityDonutChartProps {
  data: PriorityData;
  onSegmentClick?: (priority: string) => void;
}

const priorityLabels: Record<string, string> = {
  critical_1: 'Critical',
  high_2: 'High',
  moderate_3: 'Moderate',
  low_4: 'Low',
  planning_5: 'Planning',
};

const priorityColors: Record<string, string> = {
  critical_1: 'rgba(239, 68, 68, 0.8)',   // red
  high_2: 'rgba(249, 115, 22, 0.8)',      // orange
  moderate_3: 'rgba(234, 179, 8, 0.8)',   // yellow
  low_4: 'rgba(59, 130, 246, 0.8)',       // blue
  planning_5: 'rgba(16, 185, 129, 0.8)',  // green
};

const PriorityDonutChart: React.FC<PriorityDonutChartProps> = ({ data, onSegmentClick }) => {
  const priorityKeys = Object.keys(priorityLabels);
  const labels = priorityKeys.map(key => priorityLabels[key]);
  const values = priorityKeys.map(key => data[key as keyof PriorityData] || 0);
  const colors = priorityKeys.map(key => priorityColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          padding: 12,
          usePointStyle: true,
          font: { size: 11 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onSegmentClick) {
        const index = elements[0].index;
        onSegmentClick(priorityKeys[index]);
      }
    },
  };

  return (
    <div id="chart-priority-donut" className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Priority Distribution</h3>
      <div className="h-48">
        <Doughnut data={chartData} options={options} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {priorityKeys.map((priority, index) => (
          <button
            key={priority}
            onClick={() => onSegmentClick?.(priority)}
            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium hover:opacity-80 transition-opacity"
            style={{ backgroundColor: priorityColors[priority], color: 'white' }}
          >
            {priorityLabels[priority]}: {values[index]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PriorityDonutChart;
